import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import EphemeralMessage from '../StudentSingleSkill/EphemeralMessage';
import { CurrentSkillResult, shouldShowCurrentSkillStatus } from '../../custom-hooks/useCurrentSkillResult';
import { Toolbar } from 'primereact/toolbar';
import { CurrentStatus, PreviousStatus } from '../StudentSingleSkill/StudentSingleSkill';
import { PreviousAttempts } from '../../models/PreviousAttempts';
import { Tooltip } from 'primereact/tooltip';
import { RatingDisplay, SkillStatusBadge } from '../common';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Divider } from 'primereact/divider';
import { format, parseISO } from 'date-fns';
import { StudentSingleSkillQuery$data } from '../../graphQLOperations/__generated__/StudentSingleSkillQuery.graphql';
import SkillResourceHeaderLearnosityWrapper from './SkillResourceHeaderLearnosityWrapper';
import { DataLrnSessionBySkill } from '../../custom-hooks/useLrnSession';
import RevisitSkill from '../../assets/revisit-skill.svg';

interface Props {
  currentStatus: CurrentStatus | null;
  previousAttempts: PreviousAttempts[];
  currentSkillResult: CurrentSkillResult;
  previousStatus: PreviousStatus[];
  skillName: string;
  allUserActions: StudentSingleSkillQuery$data['allUserActions'];
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  disabledReviewModal: boolean;
}

const SkillResourceHeader: FC<Props> = ({
  currentStatus,
  currentSkillResult,
  previousAttempts,
  previousStatus,
  skillName,
  allUserActions,
  expanded,
  setExpanded,
  disabledReviewModal,
}) => {
  const [isDropdown, setIsDropdown] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [lrnData, setLrnData] = useState<DataLrnSessionBySkill | null>(null);
  const [showRevisitMessage, setShowRevisitMessage] = useState(false);
  const [revisitMessageKey, setRevisitMessageKey] = useState(0);
  const [revisitMessageDelay, setRevisitMessageDelay] = useState(2);

  const showStatus =
    (currentStatus || currentSkillResult) && shouldShowCurrentSkillStatus(currentSkillResult, currentStatus);
  const isReviewModalElegible = !disabledReviewModal && previousAttempts.length > 0 && previousAttempts[0].sessionId;

  useEffect(() => {
    setIsDropdown(
      previousStatus.length > 1 ||
        (previousStatus.length === 1 && previousStatus[0].id !== currentSkillResult.skillState?.skillAssessment?.id),
    );
  }, [currentSkillResult]);

  const handleOpenReviewModal = () => {
    //different behavior than AssessmentWrapper to trigger review modal from AssessmentCompleted
    setLrnData({
      sessionId: previousAttempts[0].sessionId ?? '',
      state: 'review',
      activityId: previousAttempts[0].activityId ?? '',
      showIncorrectAnswers: true,
    });
    setOpenModal(true);
  };

  const center = (
    <div className="tw-flex tw-flex-col">
      <h1>{skillName}</h1>
      {allUserActions?.nodes?.length ? (
        <span className="tw-font-atkinson tw-text-xs tw-text-neutral-700">
          Last visited: {format(parseISO(allUserActions?.nodes[0].timeStamp), 'MMM d, yyyy - h:mm a')}
        </span>
      ) : (
        <></>
      )}
    </div>
  );
  const isRevisitSkill =
    currentSkillResult?.iconType === 'revisit' ||
    currentSkillResult?.status === 'Revisit' ||
    currentStatus?.status === 'Revisit';
  const shouldShowRevisitHeader = isRevisitSkill && currentSkillResult?.hasScore && currentSkillResult?.score >= 8;

  useEffect(() => {
    if (!shouldShowRevisitHeader || !skillName) return;

    const hasSeenRevisitMessage = sessionStorage.getItem(`revisit-message-seen-${skillName}`);

    if (hasSeenRevisitMessage) return;

    setRevisitMessageDelay(2);
    setShowRevisitMessage(true);
    setRevisitMessageKey(prev => prev + 1);
    sessionStorage.setItem(`revisit-message-seen-${skillName}`, 'true');
  }, [shouldShowRevisitHeader, skillName]);

  useEffect(() => {
    if (!showRevisitMessage) return;
    const handleCloseRevisitMessage = () => {
      setShowRevisitMessage(false);
    };
    document.addEventListener('pointerdown', handleCloseRevisitMessage);
    return () => {
      document.removeEventListener('pointerdown', handleCloseRevisitMessage);
    };
  }, [showRevisitMessage]);

  const revisitTooltip = 'This is a refresher for a skill you previously passed.';

  const revisitLabel = shouldShowRevisitHeader && (
    <div className="tw-flex tw-items-center tw-justify-end tw-w-full tw-pr-8">
      <Tooltip target=".nc-revisit-skill-badge" position="top" />
      <button
        type="button"
        className="nc-revisit-skill-badge tw-inline-flex tw-items-center tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
        data-pr-tooltip={revisitTooltip}
        data-pr-position="top"
        aria-label="Show Revisit this skill message"
      >
        <SkillStatusBadge
          status="Revisit this"
          showIcon
          iconType="revisit"
          className="tw-inline-flex tw-items-center tw-gap-1 tw-text-indigo-500 tw-font-bold tw-text-base"
        />
      </button>
    </div>
  );
  const end = showStatus && !shouldShowRevisitHeader && (
    <>
      {isReviewModalElegible && (
        <Tooltip
          target=".nc_skill-result-dropdown-title"
          pt={{ root: { style: { transform: 'translateY(-20px)' } } }}
        />
      )}
      <div className={`nc_skill-result-dropdown-wrap ${expanded ? 'expanded' : ''}`}>
        <div className="nc_skill-result-dropdown">
          <div
            className={`nc_skill-result-dropdown-title tw-text-[20px] tw-font-bold ${
              isReviewModalElegible ? 'tw-cursor-pointer' : ''
            }`}
            data-pr-tooltip="Review last skill challenge result"
            data-pr-position="top"
            role="button"
            tabIndex={!isReviewModalElegible ? -1 : 0}
            onClick={() => {
              isReviewModalElegible && handleOpenReviewModal();
            }}
            onKeyDown={e => {
              if (!isReviewModalElegible) return;

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenReviewModal();
              }
            }}
          >
            Your Result: &nbsp;&nbsp;
            {currentSkillResult &&
            currentSkillResult.hasScore &&
            currentSkillResult.source !== 'fallback' &&
            previousAttempts.length > 0 ? (
              <RatingDisplay
                score={currentSkillResult.score}
                maxScore={currentSkillResult.maxScore}
                className="!tw-text-lg"
              />
            ) : (
              <SkillStatusBadge
                status={currentSkillResult?.status || currentStatus?.status || ''}
                className={`nc_skill-result-mini !tw-text-lg ${
                  currentSkillResult?.statusClassName || currentStatus?.classname
                }`}
                showIcon={currentSkillResult?.showIcon}
                iconType={currentSkillResult?.iconType}
              />
            )}
          </div>
          {isDropdown &&
            (expanded ? (
              <ChevronUp
                className="tw-text-indigo-500 tw-cursor-pointer"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpanded(!expanded);
                  }
                }}
              />
            ) : (
              <ChevronDown
                className="tw-text-indigo-500 tw-cursor-pointer"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpanded(!expanded);
                  }
                }}
              />
            ))}
        </div>
        <Divider
          className={`${expanded ? 'tw-block' : 'tw-hidden'} tw-my-0 tw-mx-3 tw-w-auto tw-h-[2px] tw-bg-neutral-100`}
        />
        {expanded &&
          // remove the first element because its the same of the currentStatus
          previousStatus.slice(1).map((el, key) => (
            <div key={key} className="nc_skill-result-dropdown-items">
              <span className="tw-text-xs tw-font-atkinson tw-text-neutral-700 tw-mr-4 ">
                {format(parseISO(el.date), 'MMM d, yyyy - h:mm a')}
              </span>
              <RatingDisplay score={el.score} maxScore={el.maxScore} className="!tw-text-lg" />
            </div>
          ))}
      </div>
    </>
  );

  return (
    <>
      <div className="col-12 col-offset-0 tw-pb-5">
        <Toolbar
          start={center}
          center={shouldShowRevisitHeader ? revisitLabel : undefined}
          end={end}
          className={`nc_skill-result-header nc_page-header ${showStatus ? '' : ' !tw-h-auto lg:!tw-h-[90px]'}`}
          pt={{
            start: {
              style: {
                position: `${expanded ? 'absolute' : ''}`,
              },
            },
          }}
        />
      </div>
      {shouldShowRevisitHeader && showRevisitMessage && (
        <EphemeralMessage
          key={revisitMessageKey}
          delay={revisitMessageDelay}
          text={
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
              <img src={RevisitSkill} alt="Revisit Skill" />
              <span>
                You’ve passed this skill before, but it looks like you <b>need a little reinforcement.</b>
              </span>
              Try again to stay on top of your Roadmap progress!
            </div>
          }
          ease={[0.34, 1.56, 0.64, 1]}
          duration={1}
          customStyle={{
            top: '240px',
            right: '250px',
            minWidth: '402px',
          }}
          strokePosition={{
            top: '-22px',
            right: '-2px',
            left: null,
          }}
          strokeClassName="tw-scale-x-[-1]"
        />
      )}
      {!isRevisitSkill && showStatus && previousAttempts.length === 1 && (
        <EphemeralMessage
          delay={1}
          text="Now you can review your last answers here"
          ease={[0.34, 1.56, 0.64, 1]}
          duration={1}
        />
      )}
      {openModal && <SkillResourceHeaderLearnosityWrapper lrnData={lrnData} setOpenLearnosityModal={setOpenModal} />}
    </>
  );
};

export default SkillResourceHeader;
